import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Trash2, Lock } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";

const MOODS = ["calm", "grateful", "anxious", "hopeful", "tender", "fired-up"];

export default function Journal() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [mood, setMood] = useState("calm");
  const [isPublic, setIsPublic] = useState(false);
  const [reflectingId, setReflectingId] = useState(null);

  const load = async () => { const r = await api.get("/journal"); setEntries(r.data); };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    await api.post("/journal", { title: title.trim(), content: content.trim(), mood, is_public: isPublic });
    setTitle(""); setContent(""); setMood("calm"); setIsPublic(false);
    toast.success("Entry saved");
    load();
  };

  const reflect = async (id) => {
    if (!user?.is_premium && entries.filter(e=>e.ai_reflection).length >= 3) {
      toast("Free plan allows 3 reflections. Upgrade for unlimited.");
      return;
    }
    setReflectingId(id);
    try {
      const r = await api.post(`/journal/${id}/reflect`);
      setEntries(entries.map(e => e.entry_id === id ? { ...e, ai_reflection: r.data.reflection } : e));
    } catch (e) { toast.error("Could not reflect. Try again."); }
    finally { setReflectingId(null); }
  };

  const del = async (id) => {
    await api.delete(`/journal/${id}`);
    setEntries(entries.filter(e => e.entry_id !== id));
  };

  return (
    <div className="space-y-8" data-testid="journal-page">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-[#8A8D98]">Private by default</p>
        <h1 className="font-display text-4xl tracking-tight mt-2">Journal</h1>
      </div>

      <form onSubmit={save} className="soft-card p-6 space-y-4" data-testid="journal-form">
        <input value={title} onChange={e=>setTitle(e.target.value)}
          placeholder="Title (optional)"
          className="w-full bg-transparent outline-none font-display text-2xl tracking-tight placeholder:text-[#c9c6bf]"
          data-testid="journal-title" />
        <textarea value={content} onChange={e=>setContent(e.target.value)}
          rows={6} placeholder="Today I noticed…"
          className="w-full bg-transparent outline-none resize-none text-lg leading-relaxed"
          data-testid="journal-content" />
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E5E3DF]">
          <span className="text-xs uppercase tracking-widest text-[#8A8D98] mr-2">Mood</span>
          {MOODS.map(m => (
            <button type="button" key={m} onClick={()=>setMood(m)}
              className={`pill-btn text-sm ${mood===m ? "bg-[#2D3142] text-white" : "bg-[#F5F2EC] text-[#2D3142] hover:bg-white"}`}
              data-testid={`mood-${m}`}
            >{m}</button>
          ))}
          <label className="ml-auto flex items-center gap-2 text-sm text-[#5b5f6e]">
            <input type="checkbox" checked={isPublic} onChange={e=>setIsPublic(e.target.checked)} data-testid="journal-public" /> Share to community
          </label>
          <button type="submit" className="pill-btn bg-[#E07A5F] text-white hover:bg-[#D26A4F]" data-testid="journal-save">Save entry</button>
        </div>
      </form>

      {entries.length === 0 ? (
        <div className="soft-card p-10 text-center">
          <p className="font-display text-2xl">A blank page is a kind beginning.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {entries.map(e => (
            <article key={e.entry_id} className="soft-card p-6" data-testid={`entry-${e.entry_id}`}>
              <div className="flex items-center gap-2 text-xs text-[#8A8D98]">
                <span className="tag-chip bg-[#81B29A]/15 text-[#5a8b73]">{e.mood}</span>
                <span>{new Date(e.created_at).toLocaleString()}</span>
                <button onClick={()=>del(e.entry_id)} className="ml-auto text-[#8A8D98] hover:text-[#E63946]" data-testid={`entry-delete-${e.entry_id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              {e.title && <h3 className="font-display text-2xl tracking-tight mt-3">{e.title}</h3>}
              <p className="mt-3 leading-relaxed whitespace-pre-wrap text-[#2D3142]">{e.content}</p>

              {e.ai_reflection ? (
                <div className="mt-5 rounded-2xl p-5 relative overflow-hidden" style={{ background: "linear-gradient(120deg, rgba(242,204,143,0.35), rgba(129,178,154,0.2))" }}>
                  <p className="text-xs uppercase tracking-widest text-[#8A5A2B] font-semibold">Warm reflection</p>
                  <p className="mt-2 leading-relaxed">{e.ai_reflection}</p>
                </div>
              ) : (
                <button onClick={()=>reflect(e.entry_id)} disabled={reflectingId===e.entry_id}
                  className="mt-4 inline-flex items-center gap-2 pill-btn bg-[#2D3142] text-white hover:bg-[#E07A5F] text-sm"
                  data-testid={`reflect-${e.entry_id}`}
                >
                  <Sparkles className="w-4 h-4" /> {reflectingId===e.entry_id ? "Reflecting…" : "Ask for a reflection"}
                </button>
              )}
            </article>
          ))}
        </div>
      )}

      {!user?.is_premium && (
        <Link to="/app/premium" className="soft-card p-5 flex items-center gap-4 hover:border-[#E07A5F]" data-testid="upgrade-inline">
          <Lock className="w-5 h-5 text-[#E07A5F]" />
          <div className="flex-1">
            <p className="font-medium">Unlimited AI reflections with Premium</p>
            <p className="text-sm text-[#8A8D98]">Free plan includes 3 reflections. Upgrade any time.</p>
          </div>
          <span className="pill-btn bg-[#F2CC8F]/60 text-[#8A5A2B]">Upgrade</span>
        </Link>
      )}
    </div>
  );
}
